// PC端成品老花镜选择度数的步骤
'use client';

import { useLensProcessStore } from '@/app/_lens-components/store/lens-process-store';
import stepCommonClassName from '@/app/_lens-components/style/step-common';
import {
  getEqualDiffArr,
  getReaderCachePres,
  getToFix2NumArr,
  setReaderCachePres,
} from '@/app/_lens-components/utils';
import { IPrescriptionOptions } from '@/components/PrescriptionFormV2/types';
import { getEmptyPrescription } from '@/components/PrescriptionFormV2/utils';
import { useReporting } from '@/hooks/useReporting';
import { ProviderType } from '@/reporting/types';
import logger from '@/utils/client-logger';
import { EPrescriptionType } from '@/utils/enums/goods';
import { formatPrescriptionDecimalForDisplay } from '@/utils/number-format';
import { useTranslations } from '@/i18n/use-translations';
import { useLocale } from 'next-intl';
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { useOpticalProcedureTitle } from '@/app/_lens-components/copywriting/useOpticalProcedureTitle';

interface ReadersPrescriptionProps {
  onConfirm: (prescription: IPrescriptionOptions) => void;
}

// 定义组件引用类型
export interface ReadersPrescriptionRef {
  handleConfirm: () => void;
}

const ReadersPrescription = forwardRef<ReadersPrescriptionRef, ReadersPrescriptionProps>(
  function ReadersPrescription({ onConfirm }, ref) {
    const { readerSelectedIndex, setReaderSelectedIndex, lensGoodsSensorsData } =
      useLensProcessStore();
    const { trackEvent } = useReporting();
    const t = useTranslations('lens-process');
    const locale = useLocale();
    const magnificationStrengthTitle = useOpticalProcedureTitle(
      'readers_prescription',
      t('selectAMagnificationStrength'),
    );

    // 监听窗口尺寸变化. 仅允许PC端设备（宽度 1280 以上）访问此页面屏幕访问（1280以下PC端设别，给用户展示的是M端样式）
    const handleResize = () => {
      const isMobileS = window.innerWidth <= 1280;

      // 如果设备类型发生变化，刷新页面
      if (isMobileS) {
        // 使用window.location.reload()刷新页面
        window.location.reload();
      }
    };

    useEffect(() => {
      window.addEventListener('resize', handleResize);

      trackEvent(
        {
          eventName: 'PrescriptionEnterPage_View',
          properties: {
            ...(lensGoodsSensorsData || {}),
            prescription_type: EPrescriptionType.READING_GLASSES,
            prescription_type_name: EPrescriptionType.READERS,
            is_magnification_page: true,
            is_have_scan_prescription: false,
          },
        },
        [ProviderType.SENSORS],
      );
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }, []);

    useEffect(() => {
      if (readerSelectedIndex < 0) {
        const sph = getReaderCachePres();
        getDefaultIndex(sph);
      }
    }, [readerSelectedIndex]);

    // 获取默认索引
    const getDefaultIndex = (val: string) => {
      if (!val) {
        return;
      }
      const index = getToFix2NumArr(getEqualDiffArr(0.25, 6, 0.25)).findIndex(
        (item) => item.slice(1) === val,
      );
      if (index >= 0) {
        setReaderSelectedIndex(index);
      }
    };

    // 处理确认
    const handleConfirm = () => {
      if (readerSelectedIndex < 0) {
        return;
      }

      const value = options[readerSelectedIndex];
      const val = value.slice(1);
      const prescription = {
        od_sph: val,
        os_sph: val,
        // 这里仅仅是存到缓存的时候方便查看，没有其他实际用处
        presType: EPrescriptionType.READERS,
      };

      setReaderCachePres(prescription);
      onConfirm({ ...getEmptyPrescription(), ...prescription });

      trackEvent(
        {
          eventName: 'PrescriptionEnterPage_PrescriptionIntroduction_Click',
          properties: {
            ...(lensGoodsSensorsData || {}),
            prescription_type: EPrescriptionType.READING_GLASSES,
            prescription_type_name: EPrescriptionType.READERS,
            user_prescription_detail: JSON.stringify(prescription),
            btn_name: 'Next',
            is_magnification_page: true,
          },
        },
        [ProviderType.SENSORS],
      );
    };

    // 向父组件暴露方法
    useImperativeHandle(ref, () => ({
      handleConfirm,
    }));

    const onMagnifyStrengthClick = (item: string, index: number) => {
      setReaderSelectedIndex(index);

      SDPrescriptionEnterPageParamValueClick();
    };

    // 处方输入页参数值填写点击
    const SDPrescriptionEnterPageParamValueClick = () => {
      try {
        // 发送埋点
        trackEvent(
          {
            eventName: 'PrescriptionEnterPage_ParamValue_Click',
            properties: {
              ...(lensGoodsSensorsData || {}),
              prescription_type: EPrescriptionType.READING_GLASSES,
              prescription_type_name: EPrescriptionType.READERS,
              param_value_type: 'od_sph',
              is_magnification_page: true,
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (e) {
        logger.error('SDPrescriptionEnterPageParamValueClick-trackEvent', e);
      }
    };

    const options = getToFix2NumArr(getEqualDiffArr(0.25, 6, 0.25));

    return (
      <div>
        <div className="w-[620px] font-br">
          <p className={stepCommonClassName.stepTitle}>{magnificationStrengthTitle}</p>
          <ul className="flex flex-wrap justify-between items-center">
            {options.map((item, index) => (
              <li
                key={index}
                className={`mb-[24px] w-[140px] h-[36px] bg-white rounded-[20px] leading-[36px] text-[16px] border  text-center box-border cursor-pointer hover:text-primary-purple hover:border-primary-purple ${
                  index === readerSelectedIndex
                    ? 'text-primary-purple border-primary-purple'
                    : 'border-[#dce1e5]'
                } ${(index + 1) % 4 === 0 ? 'mr-0' : 'mr-[20px]'}`}
                onClick={() => onMagnifyStrengthClick(item, index)}
              >
                {formatPrescriptionDecimalForDisplay(item, locale)}
              </li>
            ))}
            <li className="mr-[20px] mb-[24px] w-[140px] h-[36px] bg-transparent rounded-[20px] leading-[36px] text-[16px] border-none text-center text-transparent box-border" />
          </ul>
        </div>
      </div>
    );
  },
);

export default ReadersPrescription;
